// ── Telegram Link Modal ─────────────────────────────────────
// Shown when the user clicks "Send to Telegram" but hasn't linked their
// Telegram account yet. Gets a one-time link token from the server,
// opens the Telegram deep link in a new tab, and polls /status until
// the user completes /start in Telegram. Once linked, auto-fires the
// onLinked callback so the caller can proceed with sending.

import React, { useEffect, useRef, useState } from 'react';
import { authFetch } from './api';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function TelegramLinkModal({ onClose, onLinked }) {
  const [phase, setPhase] = useState('loading'); // loading | ready | linking | linked | error
  const [deepLink, setDeepLink] = useState(null);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  // Fetch link token on mount
  useEffect(() => {
    let cancelled = false;
    authFetch(`${API_URL}/api/integrations/telegram/link-token`, { method: 'POST' })
      .then(r => r.ok ? r.json() : r.json().then(d => Promise.reject(d?.error || 'Failed')))
      .then(data => {
        if (cancelled) return;
        setDeepLink(data.deepLink);
        setPhase('ready');
      })
      .catch(e => {
        if (cancelled) return;
        setError(String(e));
        setPhase('error');
      });
    return () => { cancelled = true; };
  }, []);

  // Poll status every 2s once we've opened the deep link
  const startPolling = () => {
    setPhase('linking');
    window.open(deepLink, '_blank', 'noopener,noreferrer');
    pollRef.current = setInterval(async () => {
      try {
        const res = await authFetch(`${API_URL}/api/integrations/telegram/status`);
        if (!res.ok) return;
        const data = await res.json();
        if (data.connected) {
          clearInterval(pollRef.current);
          setPhase('linked');
          setTimeout(() => { onLinked?.(); }, 500);
        }
      } catch { /* keep polling */ }
    }, 2000);
  };

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  return (
    <div className="tg-link-modal-backdrop" onClick={onClose}>
      <div className="tg-link-modal" onClick={e => e.stopPropagation()}>
        <button className="tg-link-close" onClick={onClose}>✕</button>

        <div className="tg-link-icon">✈️</div>
        <h3 className="tg-link-title">Connect Telegram</h3>

        {phase === 'loading' && (
          <p className="tg-link-body">Preparing a one-time link…</p>
        )}

        {phase === 'ready' && (
          <>
            <p className="tg-link-body">
              Tap the button below to open the ThoughtClaw bot in Telegram.
              Once you press <b>Start</b> there, your overview chapters will
              be delivered as audio messages.
            </p>
            <button className="tg-link-cta" onClick={startPolling}>
              Open in Telegram
            </button>
            <p className="tg-link-hint">Link expires in 10 minutes.</p>
          </>
        )}

        {phase === 'linking' && (
          <>
            <div className="tg-link-spinner" />
            <p className="tg-link-body">
              Waiting for you to press <b>Start</b> in Telegram…
            </p>
            <a className="tg-link-cta-inline" href={deepLink} target="_blank" rel="noopener noreferrer">
              Open Telegram again
            </a>
          </>
        )}

        {phase === 'linked' && (
          <>
            <div className="tg-link-check">✓</div>
            <p className="tg-link-body">Linked! Sending your overview now…</p>
          </>
        )}

        {phase === 'error' && (
          <>
            <p className="tg-link-body tg-link-error">
              Couldn't create link: {error}
            </p>
            <button className="tg-link-cta" onClick={onClose}>Close</button>
          </>
        )}
      </div>
    </div>
  );
}
